import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { SectionCard, EmptyState } from '../../../../components/ui';
import { brandHome } from '../../brandHomeTokens';
import { SEVERITY_TONES } from '../../severityTones';
import type { IngestionJobRow } from '../../useBrandHomeData';
import { formatSyncHistoryProductLabel } from '../../formatCatalogActivity';

interface SyncHistoryCardProps {
  jobs: IngestionJobRow[];
  catalogTotal?: number;
  loading?: boolean;
}

const JOB_TYPE_LABELS: Record<string, string> = {
  full_scrape: 'Full Scrape',
  incremental_scrape: 'Incremental',
  rescrape_missing: 'Re-scrape Missing',
  api_ingestion: 'API Ingestion',
  image_hosting: 'Image Hosting',
  sync_canonical: 'Sync Canonical',
  price_refresh: 'Price Refresh',
  stock_refresh: 'Stock Refresh',
  market_pricing_refresh: 'Market Pricing',
};

const jobTypeLabel = (type?: string): string => (type ? JOB_TYPE_LABELS[type] || type : 'Ingestion');

export const SyncHistoryCard: React.FC<SyncHistoryCardProps> = ({ jobs, catalogTotal, loading }) => (
  <SectionCard dense title="Recent sync history">
    {loading ? (
      <Box sx={{ p: '11px 18px' }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={44} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    ) : jobs.length === 0 ? (
      <EmptyState size="compact" title="No ingestion jobs recorded yet." />
    ) : (
      jobs.map((job) => {
        const tone = job.status === 'completed' ? SEVERITY_TONES.positive : job.status === 'failed' ? SEVERITY_TONES.blocking : SEVERITY_TONES.info;
        return (
          <Box key={job.id} sx={{ display: 'flex', alignItems: 'center', gap: '11px', p: '11px 18px', borderBottom: `1px solid ${brandHome.color.rowHairline}` }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone.dot, flex: '0 0 auto' }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{jobTypeLabel(job.type)}</Typography>
              <Typography sx={{ fontSize: '11.5px', color: brandHome.color.faint }}>
                {job.createdAt ? new Date(job.createdAt).toLocaleString() : '—'}
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
              <Box component="span" sx={{ px: '8px', py: '1px', borderRadius: `${brandHome.radius.pill}px`, bgcolor: tone.bg, color: tone.ink, fontSize: '11px', fontWeight: 700 }}>
                {job.status}
              </Box>
              <Typography sx={{ fontSize: '11.5px', color: brandHome.color.faint }}>
                {job.status === 'failed' ? `${job.errors ?? 0} errors` : formatSyncHistoryProductLabel(job, catalogTotal)}
              </Typography>
            </Box>
          </Box>
        );
      })
    )}
  </SectionCard>
);
